import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect, cloneElement } from 'react';
import classnames from 'classnames';
import { useForkRef } from '../utils/withForwardedRef';
import { ModalManager } from '../Modal';
import { Popper, Root, Paper, TooltipElement, Corner, Line } from './Tooltip.styles';
var SHORTEST_TRANSITION_DURATION = 150;
var tooltipModalManager = new ModalManager();

var Tooltip = function Tooltip(_a) {
  var children = _a.children,
      _b = _a.disableFocusListener,
      disableFocusListener = _b === void 0 ? true : _b,
      _c = _a.disableHoverListener,
      disableHoverListener = _c === void 0 ? false : _c,
      _d = _a.disableTouchListener,
      disableTouchListener = _d === void 0 ? false : _d,
      _e = _a.interactive,
      interactive = _e === void 0 ? false : _e,
      _f = _a.enterDelay,
      enterDelay = _f === void 0 ? 300 : _f,
      _g = _a.enterTouchDelay,
      enterTouchDelay = _g === void 0 ? 700 : _g,
      _h = _a.leaveDelay,
      leaveDelay = _h === void 0 ? 0 : _h,
      _j = _a.leaveTouchDelay,
      leaveTouchDelay = _j === void 0 ? 1500 : _j,
      id = _a.id,
      onClose = _a.onClose,
      onOpen = _a.onOpen,
      className = _a.className,
      openProp = _a.open,
      _k = _a.placement,
      placement = _k === void 0 ? 'bottom' : _k,
      popperProps = _a.PopperProps,
      paperProps = _a.PaperProps,
      TransitionProps = _a.TransitionProps,
      title = _a.title,
      _l = _a.variant,
      variant = _l === void 0 ? 'primary' : _l,
      _m = _a.withCorner,
      withCorner = _m === void 0 ? true : _m,
      otherProps = __rest(_a, ["children", "disableFocusListener", "disableHoverListener", "disableTouchListener", "interactive", "enterDelay", "enterTouchDelay", "leaveDelay", "leaveTouchDelay", "id", "onClose", "onOpen", "className", "open", "placement", "PopperProps", "PaperProps", "TransitionProps", "title", "variant", "withCorner"]);

  var _o = useState(false),
      openState = _o[0],
      setOpenState = _o[1];

  var _p = useState(false),
      forceUpdate = _p[1];

  var _q = useState(null),
      childNode = _q[0],
      setChildNode = _q[1];

  var ignoreNonTouchEvents = useRef(false);
  var interactiveWrapperListeners = useRef({});
  var isControlled = useRef(openProp !== undefined).current;
  var defaultId = useRef(undefined);
  var closeTimer = useRef(undefined);
  var enterTimer = useRef(undefined);
  var leaveTimer = useRef(undefined);
  var touchTimer = useRef(undefined);
  var tooltipRef = useRef(null);
  var handleOwnRef = useCallback(function (ref) {
    setChildNode(ref);
  }, []);
  var handleRef = useForkRef(children.ref, handleOwnRef);
  useEffect(function () {
    // Fallback to this default id when possible.
    // Use the random value for client side rendering only.
    // We can't use it server-side.
    if (!defaultId.current) {
      defaultId.current = "ui-tooltip-".concat(Math.round(Math.random() * 1e5));
    } // Rerender with defaultId and childNode.


    if (openProp) {
      forceUpdate(function (n) {
        return !n;
      });
    }
  }, [openProp]);
  useEffect(function () {
    return function () {
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(enterTimer.current);
      window.clearTimeout(leaveTimer.current);
      window.clearTimeout(touchTimer.current);
    };
  }, []);
  useEffect(function () {
    var handleOpen = function handleOpen(event) {
      // The mouseover event will trigger for every nested element in the tooltip.
      // We can skip rerendering when the tooltip is already open.
      // We are using the mouseover event instead of the mouseenter event to fix a hide/show issue.
      if (!isControlled) {
        setOpenState(true);
      }

      if (onOpen) {
        onOpen(event);
      }
    };

    var handleEnter = function handleEnter(event) {
      if (ignoreNonTouchEvents.current && event.type !== 'touchstart') {
        return;
      } // Remove the title ahead of time.
      // We don't want to wait for the next render commit.
      // We would risk displaying two tooltips at the same time (native + this one).


      if (childNode) {
        childNode.setAttribute('title', '');
      }

      window.clearTimeout(enterTimer.current);
      window.clearTimeout(leaveTimer.current);

      if (enterDelay) {
        enterTimer.current = window.setTimeout(function () {
          handleOpen(event);
        }, enterDelay);
      } else {
        handleOpen(event);
      }
    };

    var handleFocus = function handleFocus(event) {
      // Workaround for https://github.com/facebook/react/issues/7769
      // The autoFocus of React might trigger the event before the componentDidMount.
      // We need to account for this eventuality.
      if (!childNode) {
        setChildNode(event.currentTarget);
      }

      handleEnter(event);
    };

    var handleClose = function handleClose(event) {
      if (!isControlled) {
        setOpenState(false);
      }

      if (onClose) {
        onClose(event);
      }

      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(function () {
        ignoreNonTouchEvents.current = false;
      }, SHORTEST_TRANSITION_DURATION);
    };

    var handleLeave = function handleLeave(event) {
      if (!disableFocusListener && event.currentTarget === document.activeElement) {
        return;
      }

      window.clearTimeout(enterTimer.current);
      window.clearTimeout(leaveTimer.current);

      if (leaveDelay) {
        leaveTimer.current = window.setTimeout(function () {
          handleClose(event);
        }, leaveDelay);
      } else {
        handleClose(event);
      }
    };

    var handleTouchStart = function handleTouchStart(event) {
      ignoreNonTouchEvents.current = true;
      window.clearTimeout(leaveTimer.current);
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(touchTimer.current);
      touchTimer.current = window.setTimeout(function () {
        handleEnter(event);
      }, enterTouchDelay);
    };

    var handleTouchEnd = function handleTouchEnd(event) {
      window.clearTimeout(touchTimer.current);
      window.clearTimeout(leaveTimer.current);
      leaveTimer.current = window.setTimeout(function () {
        handleClose(event);
      }, leaveTouchDelay);
    };

    if (!disableTouchListener) {
      childNode === null || childNode === void 0 ? void 0 : childNode.addEventListener('touchstart', handleTouchStart);
      childNode === null || childNode === void 0 ? void 0 : childNode.addEventListener('touchend', handleTouchEnd);
    }

    if (!disableHoverListener) {
      childNode === null || childNode === void 0 ? void 0 : childNode.addEventListener('mouseenter', handleEnter);
      childNode === null || childNode === void 0 ? void 0 : childNode.addEventListener('mouseleave', handleLeave);
    }

    if (!disableFocusListener) {
      childNode === null || childNode === void 0 ? void 0 : childNode.addEventListener('focus', handleFocus);
      childNode === null || childNode === void 0 ? void 0 : childNode.addEventListener('blur', handleLeave);
    }

    interactiveWrapperListeners.current = interactive ? {
      onMouseOver: handleEnter,
      onMouseLeave: handleLeave,
      onFocus: handleFocus,
      onBlur: handleLeave
    } : {};
    return function () {
      if (!disableTouchListener) {
        childNode === null || childNode === void 0 ? void 0 : childNode.removeEventListener('touchstart', handleTouchStart);
        childNode === null || childNode === void 0 ? void 0 : childNode.removeEventListener('touchend', handleTouchEnd);
      }

      if (!disableHoverListener) {
        childNode === null || childNode === void 0 ? void 0 : childNode.removeEventListener('mouseenter', handleEnter);
        childNode === null || childNode === void 0 ? void 0 : childNode.removeEventListener('mouseleave', handleLeave);
      }

      if (!disableFocusListener) {
        childNode === null || childNode === void 0 ? void 0 : childNode.removeEventListener('focus', handleFocus);
        childNode === null || childNode === void 0 ? void 0 : childNode.removeEventListener('blur', handleLeave);
      }
    };
  }, [childNode, disableTouchListener, disableHoverListener, disableFocusListener, isControlled, onOpen, onClose, enterDelay, leaveDelay, enterTouchDelay, leaveTouchDelay, interactive]);
  var open = isControlled ? openProp : openState;

  if (!title) {
    open = false;
  }

  var shouldShowNativeTitle = !open && !disableHoverListener;

  var childrenProps = __assign({
    'aria-describedby': open ? id || defaultId.current : null,
    title: shouldShowNativeTitle && typeof title === 'string' ? title : null
  }, children.props);

  return _jsxs(_Fragment, {
    children: [cloneElement(children, __assign({
      ref: handleRef
    }, childrenProps)), _jsx(Popper, __assign({
      placement: placement,
      anchorEl: childNode,
      open: childNode ? Boolean(open) : false,
      manager: tooltipModalManager,
      id: childrenProps['aria-describedby'],
      TransitionProps: __assign(__assign({
        timeout: 300,
        classNames: 'grow'
      }, TransitionProps), {
        nodeRef: tooltipRef
      })
    }, interactiveWrapperListeners.current, popperProps, {
      className: classnames(popperProps === null || popperProps === void 0 ? void 0 : popperProps.className, {
        interactive: interactive
      })
    }, {
      children: _jsx(Root, __assign({
        className: "ui-tooltip__root",
        ref: tooltipRef
      }, {
        children: _jsxs(Paper, __assign({
          rounded: false,
          background: "popover",
          shadow: "popover"
        }, paperProps, {
          className: classnames('ui-tooltip__container', paperProps === null || paperProps === void 0 ? void 0 : paperProps.className)
        }, {
          children: [withCorner && _jsx(Corner, {
            className: "ui-tooltip__corner"
          }), _jsxs(TooltipElement, __assign({}, otherProps, {
            className: classnames(className, {
              touch: ignoreNonTouchEvents.current
            })
          }, {
            children: [variant !== 'primary' && _jsx(Line, {
              className: variant
            }), title]
          }))]
        }))
      }))
    }))]
  });
};

export default Tooltip;